import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getTransactionInvoice } from "../../../network/network";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import moment from "moment";
import { AppContext } from "../../../context/AppContextProvider";
import {
  AddonName,
  ZatcaInvoiceStatus,
} from "../../../configs/constants";
import { useTranslation } from "react-i18next";
import { handleApiError } from "../../../utils/errorHandler";

const getZatcaValue = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value.value || value.id || value.name;
  return value;
};

const getQRCodeSource = (qrCode) => {
  if (!qrCode) return "";

  const value = String(qrCode).trim();

  if (!value) return "";

  if (value.startsWith("data:image/")) return value;

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("/") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("<svg")) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(value)}`;
  }

  if (value.startsWith("iVBORw0KGgo")) {
    return `data:image/png;base64,${value}`;
  }

  if (value.startsWith("/9j/")) {
    return `data:image/jpeg;base64,${value}`;
  }

  if (value.startsWith("R0lGOD")) {
    return `data:image/gif;base64,${value}`;
  }

  if (value.startsWith("PHN2Zy")) {
    return `data:image/svg+xml;base64,${value}`;
  }

  return "";
};

const getA4ZatcaStatus = (status, t) => {
  const normalizedStatus = getZatcaValue(status);

  switch (normalizedStatus) {
    case ZatcaInvoiceStatus.Pending:
    case ZatcaInvoiceStatus.Submitted:
      return {
        text: t("verifying_with_zatca"),
        color: "var(--color-info)",
      };
    case ZatcaInvoiceStatus.Accepted:
      return {
        text: t("accepted"),
        color: "var(--color-success)",
      };
    case ZatcaInvoiceStatus.AcceptedWithWarnings:
      return {
        text: t("accepted_with_warnings"),
        color: "var(--color-success)",
      };
    case ZatcaInvoiceStatus.Failed:
      return {
        text: t("zatca_invoice_report_failed"),
        color: "orange",
      };
    case ZatcaInvoiceStatus.Rejected:
      return {
        text: t("rejected_by_zatca"),
        color: "var(--color-danger)",
      };
    default:
      return normalizedStatus
        ? { text: normalizedStatus, color: "var(--color-text-muted)" }
        : null;
  }
};

const TransactionsInvoice = () => {
  const [transactionData, setTransactionData] = useState("");
  const { search } = useLocation();
  const { authUser, appConfig } = useContext(AppContext);
  const { t } = useTranslation();
  const isRtl = appConfig?.direction === "rtl";
  const getLocalizedName = (item) =>
    isRtl
      ? item?.name_ar || item?.name || item?.name_en || ""
      : item?.name_en || item?.name || item?.name_ar || "";

  const isZatcaAddonActive = authUser?.active_addons?.some(
    (addon) => addon?.id && addon?.name === AddonName.ZatcaEInvoicing,
  );

  useEffect(() => {
    const query = new URLSearchParams(search);
    const urlId = query.get("id");
    if (urlId) {
      // Get Selected transaction
      getTransactionInvoice(urlId)
        .then(function (response) {
          if (response?.data?.success === true) {
            setTransactionData(response.data.data);
          } else if (response?.data?.success === false) {
            handleApiError(response, t);
          }
        })
        .catch((error) => handleApiError(error, t));
    }
  }, [search, t]);

  const transaction = transactionData.transaction;
  const zatca = isZatcaAddonActive ? transaction?.zatca : null;
  const isZatcaInvoice = !!(isZatcaAddonActive && zatca?.is_zatca_invoice);
  const zatcaStatus = isZatcaInvoice ? getZatcaValue(zatca?.status) : null;
  const zatcaStatusInfo = getA4ZatcaStatus(zatcaStatus, t);
  const zatcaQRCode = isZatcaInvoice
    ? zatca?.qr_code || transaction?.zatca_qr_code || transactionData?.zatca_qr_code
    : "";
  const qrCodeSource =
    getQRCodeSource(zatcaQRCode) || getQRCodeSource(transactionData.qr_code);

  const renderQRCode = () => {
    if (qrCodeSource) {
      return (
        <img
          src={qrCodeSource}
          alt={t("qr_code")}
          style={{ width: "140px", margin: "10px auto" }}
        />
      );
    }

    if (
      isZatcaInvoice &&
      [ZatcaInvoiceStatus.Pending, ZatcaInvoiceStatus.Submitted].includes(
        zatcaStatus,
      )
    ) {
      return (
        <div
          style={{
            width: "140px",
            minHeight: "120px",
            margin: "10px auto",
            border: "1px dashed var(--color-black)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "11px",
            padding: "8px",
            textAlign: "center",
          }}
        >
          {t("verifying_with_zatca")}
        </div>
      );
    }

    return null;
  };

  const renderZatcaA4Status = () => {
    if (!isZatcaInvoice || !zatcaStatusInfo) return null;

    return (
      <div
        style={{
          color: zatcaStatusInfo.color,
          fontSize: "11px",
          fontWeight: 600,
          marginBottom: "8px",
        }}
      >
        {zatcaStatusInfo.text}
      </div>
    );
  };

  return (
    <>
      {transaction && (
        <div style={{ backgroundColor: "var(--color-white)", padding: "20px" }}>
          <div
            id="slip"
            style={{
              maxWidth: "550px",
              margin: "0 auto",
              width: "100%",
              padding: "10px",
              border: "1px solid var(--color-black)",
              direction: isRtl ? "rtl" : "ltr",
            }}
          >
            <div
              style={{
                textAlign: "center",
                marginTop: "10px",
                paddingLeft: "10px",
              }}
            >
              <img
                id="store-logo"
                style={{
                  maxHeight: "120px",
                  maxWidth: "200px",
                  margin: "10px auto 20px",
                }}
                src={transactionData.logo}
                alt={t("business_logo")}
              />
              <br />
            </div>
            <h3
              style={{
                fontSize: "16px",
                textAlign: "center",
                borderBottom: "1px solid var(--color-text)",
              }}
            >
              {transaction.reference_transaction ? (
                <span style={{ fontSize: "16px" }}>
                  {t("refund_invoice")}
                </span>
              ) : (
                <span style={{ fontSize: "16px" }}>
                  {t("simplified_tax_invoice")}
                </span>
              )}
            </h3>
            <div style={{ width: "100%", marginTop: "5px" }}>
              <table
                cellSpacing="0"
                cellPadding="0"
                style={{ width: "100%", fontSize: "12px" }}
              >
                <tbody>
                  <tr key="col-width-1">
                    <td style={{ width: "120px" }}></td>
                    <td></td>
                  </tr>
                  <tr key="invoice_no_row">
                    <td className="app-text-xs">
                      {transaction.reference_transaction ? (
                        <span className="app-text-xs">
                          {t("refund_invoice_no")}:
                        </span>
                      ) : (
                        <span className="app-text-xs">
                          {t("invoice_id")}:
                        </span>
                      )}
                    </td>
                    <td  className="valign-top app-text-xs">
                      {transaction.uid}
                    </td>
                  </tr>
                  {/* {transaction.reference_transaction && (
                <tr key="invoice_no_row">
                  <td style={{ fontSize: "12px" }}>
                    <span style={{ fontSize: "12px" }}>
                      مرجع معاملة الاسترداد :
                    </span>
                    <br />
                    Refunded Transaction Ref
                  </td>
                  <td style={{ fontSize: "12px" }} className="valign-top">
                    {transaction.reference_transaction.uid}
                  </td>
                </tr>
              )} */}
                  <tr key="issue_date_row">
                    <td style={{ fontSize: "12px", width: "200px" }}>
                      <span className="app-text-xs">
                        {t("issue_date")}:
                      </span>
                    </td>
                    <td
                      className="valign-top app-text-xs"
                      id="transaction-datetime"
                    >
                      {moment(transaction.created_at).format(
                        "MMM. D, YYYY hh:mm a",
                      )}
                    </td>
                  </tr>
                  {transactionData.is_vat_exempt ? (
                    <tr key="exempt_vat_row">
                      <td className="app-text-xs">
                        <span className="app-text-xs">
                          {t("vat_exempt")}:
                        </span>
                      </td>
                      <td  className="valign-top app-text-xs">
                        {transactionData.is_vat_exempt ? t("yes") : t("no")}
                      </td>
                    </tr>
                  ) : (
                    <tr key="vat_no_row">
                      <td className="app-text-xs">
                        <span className="app-text-xs">
                          {t("vat_number")}:
                        </span>
                      </td>
                      <td  className="valign-top app-text-xs">
                        {transactionData.vat}
                      </td>
                    </tr>
                  )}
                  {transaction.buyer_company_name && (
                    <tr key="vat_no_row">
                      <td className="app-text-xs">
                        <span className="app-text-xs">
                          {t("buyer_company_name")}
                        </span>
                      </td>
                      <td  className="valign-top app-text-xs">
                        {transaction.buyer_company_name}
                      </td>
                    </tr>
                  )}
                  {transaction.buyer_company_vat && (
                    <tr key="vat_no_row">
                      <td className="app-text-xs">
                        <span className="app-text-xs">
                          {t("buyer_company_vat")}
                        </span>
                      </td>
                      <td  className="valign-top app-text-xs">
                        {transaction.buyer_company_vat}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <table
                cellSpacing="0"
                cellPadding="0"
                style={{ width: "100%", fontSize: "12px" }}
              >
                <tbody>
                  <tr key="col-width-2">
                    <td style={{ width: "75px" }}></td>
                    <td></td>
                    <td style={{ width: "60px" }}></td>
                    <td className="app-w-100px"></td>
                  </tr>
                  <tr key="spacing_1_row">
                    <td
                      colSpan="4"
                      style={{
                        borderBottom: "1px solid var(--color-text)",
                        height: "10px",
                      }}
                    ></td>
                  </tr>
                  <tr key="products_header" className="products-header">
                    <td
                      style={{
                        fontSize: "12px",
                        textAlign: "left",
                        borderBottom: "1px solid var(--color-text)",
                      }}
                      colSpan="2"
                    >
                      <span className="app-text-xs">{t("item")}</span>
                    </td>
                    <td
                      style={{
                        fontSize: "12px",
                        textAlign: "right",
                        borderBottom: "1px solid var(--color-text)",
                      }}
                    >
                      <span className="app-text-xs">
                        {t("unit_price")}
                      </span>
                    </td>
                    <td
                      style={{
                        fontSize: "12px",
                        textAlign: "right",
                        borderBottom: "1px solid var(--color-text)",
                      }}
                    >
                      <span className="app-text-xs">
                        {t("price_including_tax")}
                      </span>
                    </td>
                  </tr>
                  {transaction.items.map((item) => (
                    <tr
                      key={"product_items_" + item.id}
                      className="product-items"
                    >
                      <td
                        style={{ fontSize: "12px", textAlign: "left" }}
                        colSpan="2"
                        className="valign-top"
                      >
                        <span className="app-text-xs" dir="ltr">
                          {item.quantity} x{" "}
                          <bdi dir="auto">{getLocalizedName(item)}</bdi>
                        </span>
                      </td>
                      <td
                        style={{ fontSize: "12px", textAlign: "right" }}
                        className="valign-top"
                      >
                        <CurrencyFormatter
                          value={item.price}
                          currencyCode={false}
                        />
                      </td>
                      <td
                        style={{ fontSize: "12px", textAlign: "right" }}
                        className="valign-top"
                      >
                        <CurrencyFormatter
                          value={item.subtotal}
                          currencyCode={false}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr key="spacing_2_row">
                    <td
                      colSpan="4"
                      style={{ height: "10px", borderTop: "1px solid var(--color-text)" }}
                    ></td>
                  </tr>
                  {transaction.discount &&
                    !transaction.reference_transaction && (
                      <>
                        <tr key="subtotal_row">
                          <td colSpan="3" className="app-text-xs">
                            <span className="app-text-xs">
                              {t("subtotal")}
                            </span>
                          </td>
                          <td
                            style={{ fontSize: "12px", textAlign: "right" }}
                            id="subtotal-amount-without-tax"
                          >
                            <CurrencyFormatter
                              value={
                                transaction.amount_charged -
                                transaction.tax +
                                Number(transaction.discount_amount)
                              }
                            />
                          </td>
                        </tr>
                        <tr key="discount_row">
                          <td colSpan="3" className="app-text-xs">
                            <span className="app-text-xs">
                              {t("discount")}{" "}
                              {transaction.discount.discount_percentage}%
                            </span>
                          </td>
                          <td
                            style={{ fontSize: "12px", textAlign: "right" }}
                            id="discount"
                          >
                            -{" "}
                            <CurrencyFormatter
                              value={transaction.discount_amount}
                            />
                          </td>
                        </tr>
                      </>
                    )}
                  <tr key="total_taxable_row">
                    <td colSpan="3" className="app-text-xs">
                      <span className="app-text-xs">
                        {t("total_taxable_excluding_vat")}
                      </span>
                    </td>
                    <td
                      style={{ fontSize: "12px", textAlign: "right" }}
                      id="total-amount-without-tax"
                    >
                      <CurrencyFormatter
                        value={transaction.amount_charged - transaction.tax}
                      />
                    </td>
                  </tr>
                  <tr key="total_vat_row">
                    <td colSpan="3" className="app-text-xs">
                      <span className="app-text-xs">{t("vat_total")}</span>
                    </td>
                    <td
                      style={{ fontSize: "12px", textAlign: "right" }}
                      id="total-tax"
                    >
                      <CurrencyFormatter value={transaction.tax} />
                    </td>
                  </tr>
                  <tr key="spacing_3_row">
                    <td
                      colSpan="4"
                      style={{ height: "10px", borderTop: "1px solid var(--color-text)" }}
                    ></td>
                  </tr>
                  <tr key="total_amount_row">
                    <td colSpan="2" style={{ fontSize: "16px" }}>
                      <span style={{ fontSize: "16px" }}>
                        {t("total_amount_due")}
                      </span>
                    </td>
                    <td
                      style={{ textAlign: "right", fontSize: "16px" }}
                      colSpan="2"
                      id="total-amount"
                    >
                      <CurrencyFormatter value={transaction.amount_charged} />
                    </td>
                  </tr>
                  <tr key="spacing_4_row">
                    <td colSpan="4" style={{ height: "20px" }}></td>
                  </tr>
                </tbody>
              </table>
              <div className="app-text-center">
                {renderQRCode()}
                {renderZatcaA4Status()}
                <br />
                <small>{t("printed_by_anypos")}</small>
                <br />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TransactionsInvoice;
